/**
 * Data model for the Improvement Board prototype.
 *
 * Self-contained by design: no API, no redux. `/design` renders outside the
 * auth gate (see the comment in App.tsx), so `state.user.me` is an empty object
 * there — the personas below stand in for the signed-in user.
 */

export type IdeaType = 'bug' | 'feature' | 'data';

export type Persona = {
    id: string,
    name: string,
    role: string,
}

/** Stand-ins for the signed-in user. Switching persona is what makes the
 *  one-vote-per-employee rule demonstrable. */
export const PERSONAS: Persona[] = [
    { id: 'u-demo', name: 'Demo User', role: 'Core Solutions' },
    { id: 'u-anna', name: 'Anna B.', role: 'Line Supervisor KEF' },
    { id: 'u-jon', name: 'Jón K.', role: 'Hangar Planner' },
    { id: 'u-eva', name: 'Eva S.', role: 'Tool Store' },
    { id: 'u-omar', name: 'Ómar T.', role: 'Mechanic, Bay 2' },
];

export const TYPE_OPTIONS: { value: IdeaType, label: string }[] = [
    { value: 'bug', label: 'Bug fix' },
    { value: 'feature', label: 'New feature' },
    { value: 'data', label: 'Data or integration' },
];

export const TYPE_LABEL: Record<IdeaType, string> = {
    bug: 'Bug fix',
    feature: 'New feature',
    data: 'Data / integration',
};

/** Mirrors the real sidebar labels so a submission points at a place people recognise. */
export const AREA_OPTIONS = [
    'Hangar Plan', 'Hangar Overview', 'Shop Plan', 'Line KEF', 'Line RKV', 'Line Planner',
    'Maintenance Control', 'Bay Handover', 'Staff Management', 'Task Assignment',
    'Certificates', 'Tools Register', 'Tools Stock', 'World Tools', 'Capability',
    'Warranty', 'Extra Payments', 'Clothes Requests', 'I/O Checklists', 'Security',
    'My Profile', 'Somewhere else / not sure',
].map(a => ({ value: a, label: a }));

export const ROLE_OPTIONS = [
    'Mechanics', 'Line supervisors', 'Hangar planners', 'Shop staff',
    'Tool store', 'Maintenance control', 'Administration', 'Almost everyone',
].map(r => ({ value: r, label: r }));

/**
 * Frequency as occurrences per person per year. Rough on purpose - the point is
 * an order of magnitude that makes people sanity-check their own estimate, not
 * an accounting figure.
 */
export const FREQUENCY_OPTIONS: { value: string, label: string, perYear: number }[] = [
    { value: 'many-daily', label: 'Several times a day', perYear: 750 },
    { value: 'daily', label: 'About once a day', perYear: 250 },
    { value: 'weekly', label: 'A few times a week', perYear: 120 },
    { value: 'monthly', label: 'A few times a month', perYear: 24 },
    { value: 'rarely', label: 'A few times a year', perYear: 4 },
];

export type Idea = {
    id: string,
    title: string,
    type: IdeaType,
    area: string,
    roles: string,
    peopleAffected: number,
    frequency: string,
    minutesEach: number,
    impact: string,
    /** Bug branch. */
    whatHappens?: string,
    whatShould?: string,
    repro?: string,
    /** Feature and data branch. */
    whatIsIt?: string,
    whoAndWhy?: string,
    today?: string,
    submittedBy: string,
    submittedAt: string,
    /** Voter ids rather than a count, so one-per-employee is enforceable. */
    votes: string[],
}

/** people x occurrences/year x minutes, in hours. */
export const hoursPerYear = (idea: Pick<Idea, 'peopleAffected' | 'frequency' | 'minutesEach'>): number => {
    const freq = FREQUENCY_OPTIONS.find(f => f.value === idea.frequency)?.perYear ?? 0;
    return Math.round((idea.peopleAffected * freq * idea.minutesEach) / 60);
};

export const formatHours = (hours: number): string =>
    hours >= 1000 ? `${Math.round(hours / 100) / 10}k h/yr` : `${hours} h/yr`;

const seeds: Idea[] = [
    {
        id: 'seed-1',
        title: 'Show MXI part availability directly on the task card',
        type: 'data', area: 'Hangar Plan', roles: 'Mechanics',
        peopleAffected: 40, frequency: 'daily', minutesEach: 6,
        impact: 'Mechanics stop walking to the tool store to find out a part is not there. Planners stop being asked the same question twenty times a day, and a task that cannot start is visible before anyone is assigned to it.',
        whatIsIt: 'Pull the part availability we already show in MXI into the task card in Hangar Plan, as a small badge with the count and a red state when something is missing.',
        whoAndWhy: 'Mechanics and hangar planners. Right now you only find out a part is missing after you have opened MXI in a separate tab, which means the task is already assigned and the slot is already lost.',
        today: 'We open MXI in another tab, search the work order, and check the parts list by hand for every task we are about to start.',
        submittedBy: 'Ómar T.', submittedAt: '2026-09-02',
        votes: ['u-anna', 'u-jon', 'u-eva', 'u-omar'],
    },
    {
        id: 'seed-2',
        title: 'Handover report loses unsaved notes when the dialog is closed',
        type: 'bug', area: 'Line KEF', roles: 'Line supervisors',
        peopleAffected: 12, frequency: 'weekly', minutesEach: 15,
        impact: 'Supervisors retype notes at the end of a shift, which is exactly when they are most likely to give up and write less. The handover quality drops on the shifts where it matters most.',
        whatHappens: 'If you type general notes in the handover dialog and close it with the X or by clicking the backdrop instead of pressing Save notes, everything you typed is gone. There is no warning and no draft kept.',
        whatShould: 'Either keep the text when the dialog reopens, or warn that there are unsaved notes before closing.',
        repro: 'Open a Line KEF handover, type into General notes, click the X. Reopen the same report.',
        submittedBy: 'Anna B.', submittedAt: '2026-09-11',
        votes: ['u-demo', 'u-anna', 'u-omar'],
    },
    {
        id: 'seed-3',
        title: 'Bulk-assign mechanics to several tasks at once',
        type: 'feature', area: 'Hangar Plan', roles: 'Hangar planners',
        peopleAffected: 6, frequency: 'daily', minutesEach: 12,
        impact: 'Morning planning is the tightest window of the day. Assigning a crew of six to eight tasks one at a time is the single slowest step in it.',
        whatIsIt: 'Select several tasks with checkboxes, then assign the same mechanic or group to all of them in one action.',
        whoAndWhy: 'Hangar planners during the morning plan. The same crew usually covers a block of related tasks, so doing it per task is pure repetition.',
        today: 'We open each task, search the employee, assign, close, repeat. For a full bay that is forty or fifty clicks.',
        submittedBy: 'Jón K.', submittedAt: '2026-09-15',
        votes: ['u-jon', 'u-demo', 'u-eva'],
    },
    {
        id: 'seed-4',
        title: 'Tool loan receipts should be printable as a single page',
        type: 'feature', area: 'Tools Stock', roles: 'Tool store',
        peopleAffected: 4, frequency: 'weekly', minutesEach: 10,
        impact: 'A printed receipt is what we hand over with the tools. Building it by hand from the screen is slow and gets the serial numbers wrong often enough to matter.',
        whatIsIt: 'A print view that lists the loaned tools, the employee, the date and the expected return, on one page.',
        whoAndWhy: 'Tool store staff and whoever signs for the tools. Today the paper trail is reconstructed manually.',
        today: 'We screenshot the loaned-out list and paste it into Word, then fix the layout before printing.',
        submittedBy: 'Eva S.', submittedAt: '2026-09-18',
        votes: ['u-eva', 'u-omar'],
    },
    {
        id: 'seed-5',
        title: 'Certificate expiry warning comes too late to act on',
        type: 'bug', area: 'Certificates', roles: 'Almost everyone',
        peopleAffected: 90, frequency: 'monthly', minutesEach: 20,
        impact: 'A certificate that lapses takes a mechanic off the floor until it is renewed. Catching it a month earlier is the difference between planning around it and losing a shift.',
        whatHappens: 'The expiring badge on My Profile only appears inside the last month. By then the renewal course may already be full.',
        whatShould: 'Warn at three months, and again at one month, with the renewal date visible rather than just a coloured icon.',
        repro: 'Open My Profile with a certificate expiring in six weeks. No warning is shown.',
        submittedBy: 'Anna B.', submittedAt: '2026-09-20',
        votes: ['u-anna', 'u-jon'],
    },
    {
        id: 'seed-6',
        title: 'Show the aircraft tail number in the browser tab title',
        type: 'feature', area: 'Line KEF', roles: 'Line supervisors',
        peopleAffected: 12, frequency: 'many-daily', minutesEach: 1,
        impact: 'Small, but it happens constantly. With six tabs open on a shift, finding the right one is guesswork.',
        whatIsIt: 'Put the tail number and page name in document.title so tabs are distinguishable.',
        whoAndWhy: 'Anyone who works with more than one aircraft at a time, which on line is everyone.',
        today: 'We click through tabs until we find the right aircraft.',
        submittedBy: 'Ómar T.', submittedAt: '2026-09-22',
        votes: ['u-omar'],
    },
];

const STORAGE_KEY = 'maintener.design.improvements';

/**
 * Its own key, deliberately. `maintener.design.prefs` is persisted by
 * ThemeProvider with a wholesale JSON.stringify of its own state, and two
 * provider instances are live on a /design route - anything foreign stored
 * there is clobbered on the next theme change.
 */
export const loadIdeas = (): Idea[] => {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        return raw ? (JSON.parse(raw) as Idea[]) : seeds;
    } catch {
        // Private mode or disabled storage - fall back to the seeds rather than crash.
        return seeds;
    }
};

export const saveIdeas = (ideas: Idea[]): void => {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ideas));
    } catch {
        // Persistence is a nicety in a prototype, not a requirement.
    }
};

export const resetIdeas = (): Idea[] => {
    try {
        window.localStorage.removeItem(STORAGE_KEY);
    } catch {
        // Ignore - the caller still gets the seeds back.
    }
    return seeds;
};
